"use client";

import { useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface LimitReachedDialogProps {
    isOpen: boolean;
    onClose: () => void;
    remainingTime?: string; // e.g., "23 hours"
}

export default function LimitReachedDialog({ isOpen, onClose, remainingTime }: LimitReachedDialogProps) {
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';

    // Close on ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop with blur */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
                onClick={onClose}
            />

            {/* Dialog */}
            <div
                className={`relative w-full max-w-md mx-4 rounded-2xl shadow-2xl animate-slide-up overflow-hidden ${isDarkMode
                        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-white/10'
                        : 'bg-white border border-slate-200'
                    }`}
            >
                {/* Gradient accent top */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500" />

                {/* Content */}
                <div className="p-6 pt-8">
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${isDarkMode
                                ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30'
                                : 'bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200'
                            }`}>
                            <span className="text-4xl">⏰</span>
                        </div>
                    </div>

                    {/* Title */}
                    <h2 className={`text-2xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                        Daily Limit Reached
                    </h2>

                    {/* Subtitle */}
                    <p className={`text-center mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                        You&apos;ve used your 2 free AI enhancements for today.
                    </p>

                    {/* Info box */}
                    <div className={`rounded-xl p-4 mb-6 ${isDarkMode
                            ? 'bg-white/5 border border-white/10'
                            : 'bg-slate-50 border border-slate-200'
                        }`}>
                        <div className="flex items-start gap-3">
                            <span className="text-xl mt-0.5">✨</span>
                            <div>
                                <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'
                                    }`}>
                                    Your limit resets at midnight
                                </p>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'
                                    }`}>
                                    {remainingTime
                                        ? `Come back in ${remainingTime} for more AI-powered improvements!`
                                        : 'Come back tomorrow for more AI-powered resume improvements!'
                                    }
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Tips */}
                    <div className={`rounded-xl p-4 mb-6 ${isDarkMode
                            ? 'bg-purple-500/10 border border-purple-500/20'
                            : 'bg-purple-50 border border-purple-200'
                        }`}>
                        <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'
                            }`}>
                            💡 In the meantime, you can:
                        </p>
                        <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-purple-200/80' : 'text-purple-600'
                            }`}>
                            <li>• Edit your resume manually</li>
                            <li>• Use the AI Coach for advice</li>
                            <li>• Download your current resume</li>
                        </ul>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${isDarkMode
                                    ? 'bg-white/10 hover:bg-white/15 text-white border border-white/10'
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
                                }`}
                        >
                            Got it
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 py-3 px-4 rounded-xl font-semibold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white transition-all shadow-lg shadow-purple-500/25"
                        >
                            Continue Editing
                        </button>
                    </div>
                </div>

                {/* Close button */}
                <button
                    onClick={onClose}
                    className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isDarkMode
                            ? 'hover:bg-white/10 text-slate-400 hover:text-white'
                            : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                        }`}
                >
                    ✕
                </button>
            </div>
        </div>
    );
}
